// ControlRenderer.tsx
import React, { useEffect, useState } from 'react';
import TextControl from './controls/TextControl';
import TextAreaControl from './controls/TextAreaControl';
import TabControl from './controls/TabControl';
import GridControl from './controls/GridControl';
import DataGridControl from './controls/DataGridControl';
import FlexControl from './controls/FlexControl';
import LabelControl from './controls/LabelControl';
import DropdownControl from './controls/DropdownControl';
import ButtonControl from './controls/ButtonControl';
import DateControl from './controls/DateControl';
import { evaluateExpression, get, set } from './utils/Utils';
import { FormModeType, useFormData } from '../../components/FormDataProvider';
import UploadControl from './controls/UploadControl';
import CheckboxControl from './controls/CheckboxControl';
import { Breakpoint } from '@mui/material';
import { Rule, validate, ValidationResult } from './utils/Validation';
import { useValidation, ValidationProvider } from '../ValidationProvider';
import { useAuth } from '../AuthProvider';
import FormControl from './controls/FormControl';

/**
 * Controls the visibility of a UI element based on conditions.
 */
interface Visibility {
  /** 
   * Expression associated with the control's display logic.
   * The control is displayed only if the expression evaluates to true.
   */
  expression?: string;

  /** 
   * List of roles that requied to display this control.
   * The control is displayed only if current role set intersects with the required set.
   */
  requiredRoles?: string[];

  /** 
   * Minimum screen size (in pixels or breakpoint) where the control is displayed.
   */
  minScreenSize?: Breakpoint | number;

  /**
   * Maximum screen size (in pixels or breakpoint) where the control is displayed.
   */
  maxScreenSize?: Breakpoint | number;

  /**
   * Determines the mode to be used when the control is hidden.
   */
  hiddenMode?: FormModeType;
}

/**
* Describes the grid position of a UI control.
*/
interface GridPosition {
  /** Specifies the row position of the control in the grid. */
  row: number;

  /** Specifies the column position of the control in the grid. */
  column: number;

  /** Number of rows the control spans across. */
  rowSpan?: number;

  /** Number of columns the control spans across. */
  colSpan?: number;

  /** Number of controls that span in a straight line. */
  controlSpan?: number;

  /** Determines if the control should be shown in mobile view. */
  showInMobile?: boolean;
}

/**
* Represents a UI control with configurable properties.
*/
interface Control {
  /** Type of the control (e.g., 'button', 'text', 'dropdown'). */
  type: string;

  /** Label displayed for the control. */
  label?: string;

  /** Name identifier for the control. */
  name: string;

  /** Key for data context associated with this control. */
  dataContextKey?: string;

  /** Key for specific data related to this control. */
  dataKey?: string;

  /** Validation rules applied to this control. */
  validation?: Rule;

  /** Visibility settings for this control. */
  visibility?: Visibility;

  /** Grid positioning attributes for the control's layout. */
  gridPosition?: GridPosition;

  /** Horizontal alignment of the control. */
  horizontalAlignment?: string; // 'left' | 'center' | 'right' | 'stretch';

  /** Vertical alignment of the control. */
  verticalAlignment?: string; // 'top' | 'middle' | 'bottom' | 'stretch';

  /** Minimum width of the control. */
  minWidth?: number;

  /** Mode that overrides the default control mode. */
  mode?: FormModeType;

  /** Nested controls within this control. */
  controls?: Control[];
}

interface ControlRendererProps {
  control: Control;
  data: any; // Replace 'any' with a more specific type based on your data structure
  onDataChange: (newData: any) => void; // Replace 'any' with a more specific type based on your data structure
  mode: FormModeType;
  onParentMenuClick?: (event: React.MouseEvent<HTMLElement>) => void; // called when a parent menu is clicked
}

const ControlRenderer: React.FC<ControlRendererProps> = React.memo(({ control, data, onDataChange, mode, onParentMenuClick }) => {
  const { formData } = useFormData();
  const { authorize } = useAuth();

  const dataContext = control.dataContextKey ? get(formData, control.dataContextKey, formData) : data;

  const firstElement = (data: any) => {
    if (Array.isArray(data)) {
      if (data.length == 0) {
        console.log(`creating a missing first element for ${control.type} ${control.name} ${control.label}`);
        data.push({});
      }
      return data[0];
    }
    return data;
  };

  const handleDataChange = (newData: any) => {
    onDataChange(newData);
  };

  const CreateGridContext = (data: any) => {
    if (control.dataKey) {
      var gridContext = get(data, control.dataKey, undefined);
      if (!gridContext) {
        set(data, control.dataKey, gridContext)
        console.log(`creating a missing datacontext ${control.dataKey}`);
      }
    }
  };

  let validationRule = control.validation;
  if (!validationRule && control.dataContextKey && control.dataKey) {
    validationRule = get(get(formData, "Validation"), control.dataKey, {});
  }

  const withValidation = (ControlComponent: React.JSX.Element) => {
    return (
      validationRule && (mode === "edit" || mode == "design") ? (
        <ValidationProvider rule={validationRule}>
          {ControlComponent}
        </ValidationProvider>
      ) : (
        ControlComponent
      )
    );
  };

  // Only render control if it's visible
  let renderMode = mode;
  try {
    if (control.visibility?.expression && !evaluateExpression(control.visibility.expression, firstElement(dataContext))) {
      renderMode = control.visibility?.hiddenMode? control.visibility?.hiddenMode : "hidden"; // If the expression evaluates to false, set mode to hidden
    }
    if (mode !== "design" && control.visibility?.requiredRoles && !authorize(control.visibility.requiredRoles)) {
      renderMode = control.visibility?.hiddenMode? control.visibility?.hiddenMode : "hidden"; // If the user does not have the required roles, set mode to hidden
    }
  } catch (error: any) {
    console.error('Error evaluating expression:', error);
  }

  if (renderMode === 'hidden') {
      return null; // Do not render the control if it's not visible
  }

  switch (control.type) {
    case 'label':
      return <LabelControl control={control} mode={renderMode} />;
    case 'text':
      return withValidation(<TextControl control={control} data={firstElement(dataContext)} onDataChange={handleDataChange} mode={renderMode} />);
    case 'textarea':
      return withValidation(<TextAreaControl control={control} data={firstElement(dataContext)} onDataChange={handleDataChange} mode={renderMode} />);
    case 'select':
      return withValidation(<DropdownControl control={control} data={firstElement(dataContext)} onDataChange={handleDataChange} mode={renderMode} />);
    case 'button':
    case 'gridbutton':
    case 'link':
      return withValidation(<ButtonControl control={control} data={firstElement(dataContext)} mode={renderMode} />);
    case 'upload':
      return withValidation(<UploadControl control={control} data={dataContext} mode={renderMode} />);
    case 'tab':
      return withValidation(<TabControl control={control} data={dataContext} onDataChange={handleDataChange} mode={renderMode} onParentMenuClick={onParentMenuClick} />);
    case 'flex':
      CreateGridContext(dataContext);
      return withValidation(<FlexControl control={control} data={firstElement(dataContext)} onDataChange={handleDataChange} mode={renderMode} onParentMenuClick={onParentMenuClick} />);
    case 'grid':
      CreateGridContext(dataContext);
      return withValidation(<GridControl control={control} data={firstElement(dataContext)} onDataChange={handleDataChange} mode={renderMode} onParentMenuClick={onParentMenuClick} />);
    case 'datagrid':
      //console.log(control)
      //console.log(dataContext);
      return (control.controls &&
        <DataGridControl
          control={control}
          data={dataContext}
          onDataChange={handleDataChange}
          mode={renderMode}
          onParentMenuClick={onParentMenuClick}
        />);
    case 'date':
      return withValidation(<DateControl control={control} data={firstElement(dataContext)} onDataChange={handleDataChange} mode={renderMode} />);
    case 'form':
      // just render the first control using the specified dataKey
      return withValidation(<FormControl control={control} data={dataContext} onDataChange={handleDataChange} mode={renderMode} onParentMenuClick={onParentMenuClick} />);
    case 'check':
      return withValidation(<CheckboxControl control={control} data={firstElement(dataContext)} onDataChange={handleDataChange} mode={renderMode} />);
    // Add cases for other control types as needed
    default:
      return (<div>{control.type} is not implemented</div>);
  }
});

ControlRenderer.displayName = 'ControlRenderer';

export type { Control, GridPosition };
export default ControlRenderer;

